/**
 * A ficha do Grupo de Empresas (13.9, FR-88): as filiais e a soma dos Negócios, Orçamentos e
 * Pedidos de todas — o que quem olha **já** enxerga.
 *
 * **Composta pelo SPA, chamando os donos** (AD-25, sem BFF): as filiais vêm do `cadastro`
 * (`GET /grupos/{id}`, só as que a RLS entrega), os Negócios de todas numa leitura em lote do
 * `negocios` (`GET /negocios/das-empresas`), e os Orçamentos de **cada** Negócio com
 * `com_orcamento` pela rota existente do `integracao-erp` (`GET /negocios/{id}/orcamentos`) — o
 * lote no `integracao-erp` fica para quando doer.
 *
 * **O valor aparece só quando as respostas trazem a chave `valor`**: a SDR não a recebe (FR-62), e
 * a tela nunca decide sigilo pelo perfil. As somas são em centavos inteiros; `valor: null` ("sem
 * orçamento") não soma e não esconde a coluna.
 *
 * As escritas (renomear, incluir, retirar, desfazer) aparecem com `gerir_grupos_de_empresas` —
 * incluir e retirar também com `editar_empresa` —, e a recusa do serviço vai à tela verbatim.
 *
 * **Desde a 13.14b:** o código do Negócio abre o modal **sobre esta ficha** (`LinkDoNegocio`, o
 * `?negocio=` da 11.4 — DW-435), e fechar volta a ela; e a tabela soma também o **valor dos ganhos**
 * ("Valor ganho", DW-437), pela mesma regra da chave `valor` — a SDR não vê coluna nenhuma.
 */

import { useQueries } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import { type FormEvent, useMemo, useState } from 'react'

import { ErroDaApi, MENSAGEM_DE_ERRO_DESCONHECIDO } from '@/api/erros'
import { useResponsaveis } from '@/modulos/identidade/api'
import { useSessao } from '@/modulos/identidade/sessao'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import { listarOrcamentosDoNegocio } from '@/modulos/integracao-erp/api'
import { type NegocioDasEmpresas, useNegociosDasEmpresas } from '@/modulos/negocios/api'
import { MOEDA_EXATA } from '@/modulos/negocios/apresentacao'
import { LinkDoNegocio } from '@/modulos/negocios/navegacao-do-negocio'
import { CampoDeEmpresa, documentoNaSugestao } from './CampoDeEmpresa'
import {
  type FilialDoGrupo,
  TAMANHO_DO_NOME_DO_GRUPO,
  useDesfazerGrupo,
  useGrupo,
  useIncluirFilial,
  useRenomearGrupo,
  useRetirarFilial,
} from './api'
import { LENDO_A_EMPRESA, useEscolhaDeEmpresa } from './escolha-da-empresa'
import { NOME_LONGO_DEMAIS, tamanhoDoNome } from './GrupoDaEmpresa'
import { ModalDaNovaEmpresa } from './ModalDaNovaEmpresa'
import { nomeExibidoDaEmpresa } from './nome-da-empresa'
import './grupo.css'

export const GRUPO_NAO_ENCONTRADO = 'Grupo não encontrado.'
export const NOTA_DA_INCLUSAO = 'Quem é Responsável pela empresa incluída passa a acompanhar o grupo.'
export const CONFIRMACAO_DO_DESFAZER =
  'Todas as filiais saem do grupo, inclusive as que você não vê. Desfazer o grupo?'
const SEM_VALOR = '—'
const ID_DO_INCLUIR = 'grupo-incluir'

const ROTULO_DO_STATUS: Record<NegocioDasEmpresas['status'], string> = {
  em_andamento: 'Em andamento',
  ganho: 'Ganho',
  perdido: 'Perdido',
}

/** `"148000.00"` → `14800000`. Texto do serviço, sem ponto flutuante no caminho. */
export function centavos(valor: string): number {
  const [inteiros = '0', casas = ''] = valor.split('.')
  const sinal = inteiros.startsWith('-') ? -1 : 1
  const absoluto = Number(inteiros.replace('-', '')) * 100 + Number((casas + '00').slice(0, 2))
  return sinal * absoluto
}

function emReais(totalEmCentavos: number): string {
  return MOEDA_EXATA.format(totalEmCentavos / 100)
}

function mensagemDe(erro: unknown): string {
  return erro instanceof ErroDaApi ? erro.message : MENSAGEM_DE_ERRO_DESCONHECIDO
}

type Soma = {
  andamento: number
  ganhos: number
  perdidos: number
  pedidos: number
  orcamentos: number
  valorEmAndamento: number
  valorGanho: number
  valorDosOrcamentos: number
}

const SOMA_VAZIA: Soma = {
  andamento: 0,
  ganhos: 0,
  perdidos: 0,
  pedidos: 0,
  orcamentos: 0,
  valorEmAndamento: 0,
  valorGanho: 0,
  valorDosOrcamentos: 0,
}

function somar(a: Soma, b: Soma): Soma {
  return {
    andamento: a.andamento + b.andamento,
    ganhos: a.ganhos + b.ganhos,
    perdidos: a.perdidos + b.perdidos,
    pedidos: a.pedidos + b.pedidos,
    orcamentos: a.orcamentos + b.orcamentos,
    valorEmAndamento: a.valorEmAndamento + b.valorEmAndamento,
    valorGanho: a.valorGanho + b.valorGanho,
    valorDosOrcamentos: a.valorDosOrcamentos + b.valorDosOrcamentos,
  }
}

export function FichaDoGrupo({ grupoId }: { grupoId: string }) {
  const grupo = useGrupo(grupoId)
  const sessao = useSessao().data
  const responsaveis = useResponsaveis()
  const navegar = useNavigate()
  const filiais = useMemo(() => grupo.data?.filiais ?? [], [grupo.data])
  const negocios = useNegociosDasEmpresas(filiais.map((filial) => filial.id))
  const permissoes = sessao?.permissoes ?? {}
  const podeGerir = 'gerir_grupos_de_empresas' in permissoes
  const podeEditarFilial = podeGerir && 'editar_empresa' in permissoes
  /* 13.15 (L): "Criar nova empresa" no "Incluir filial" — só com `criar_empresa` (conveniência). */
  const podeCriarEmpresa = 'criar_empresa' in permissoes
  const veEspelho = 'ver_espelho_de_orcamento' in permissoes

  const comOrcamento = useMemo(
    () => (veEspelho ? (negocios.data ?? []).filter((negocio) => negocio.com_orcamento) : []),
    [negocios.data, veEspelho],
  )
  const orcamentos = useQueries({
    queries: comOrcamento.map((negocio) => ({
      queryKey: ['integracao-erp', 'orcamentos', negocio.id] as const,
      queryFn: () => listarOrcamentosDoNegocio(negocio.id),
      retry: false,
    })),
  })

  const renomear = useRenomearGrupo(grupoId)
  const incluir = useIncluirFilial(grupoId)
  const retirar = useRetirarFilial(grupoId)
  const desfazer = useDesfazerGrupo(grupoId)
  const [renomeando, setRenomeando] = useState(false)
  const [nome, setNome] = useState('')
  const [erroDoNome, setErroDoNome] = useState<string | null>(null)
  const inclusao = useEscolhaDeEmpresa(ID_DO_INCLUIR)
  const escolhida = inclusao.escolhida
  const [confirmando, setConfirmando] = useState(false)

  const falhasDosOrcamentos = orcamentos.filter((consulta) => consulta.isError).length
  const orcamentosCarregando = orcamentos.some((consulta) => consulta.isLoading)
  const orcamentosLidos = veEspelho && falhasDosOrcamentos === 0 && !orcamentosCarregando

  const { porFilial, total, valorDosNegocios, valorDosOrcamentos } = useMemo(() => {
    const mapa = new Map<string, Soma>()
    let comValorNosNegocios = false
    let comValorNosOrcamentos = false
    for (const negocio of negocios.data ?? []) {
      const soma = { ...(mapa.get(negocio.empresa_id) ?? SOMA_VAZIA) }
      if (negocio.status === 'em_andamento') soma.andamento += 1
      if (negocio.status === 'ganho') soma.ganhos += 1
      if (negocio.status === 'perdido') soma.perdidos += 1
      if (negocio.numero_do_pedido !== null) soma.pedidos += 1
      if ('valor' in negocio) {
        comValorNosNegocios = true
        if (negocio.status === 'em_andamento' && typeof negocio.valor === 'string') {
          soma.valorEmAndamento += centavos(negocio.valor)
        }
        //: 13.14b (DW-437): o que o cliente já comprou — o valor congelado de cada ganho.
        if (negocio.status === 'ganho' && typeof negocio.valor === 'string') {
          soma.valorGanho += centavos(negocio.valor)
        }
      }
      mapa.set(negocio.empresa_id, soma)
    }
    comOrcamento.forEach((negocio, indice) => {
      const itens = orcamentos[indice]?.data?.itens ?? []
      const soma = { ...(mapa.get(negocio.empresa_id) ?? SOMA_VAZIA) }
      soma.orcamentos += itens.length
      for (const orcamento of itens) {
        if ('valor' in orcamento) {
          comValorNosOrcamentos = true
          if (typeof orcamento.valor === 'string') {
            soma.valorDosOrcamentos += centavos(orcamento.valor)
          }
        }
      }
      mapa.set(negocio.empresa_id, soma)
    })
    let totalDoGrupo = SOMA_VAZIA
    for (const filial of filiais) {
      totalDoGrupo = somar(totalDoGrupo, mapa.get(filial.id) ?? SOMA_VAZIA)
    }
    return {
      porFilial: mapa,
      total: totalDoGrupo,
      valorDosNegocios: comValorNosNegocios,
      valorDosOrcamentos: comValorNosOrcamentos,
    }
  }, [negocios.data, comOrcamento, orcamentos, filiais])

  //: A coluna de valor existe quando **alguma** resposta trouxe a chave; a célula do lado que não a
  //: trouxe é "—", e não um R$ 0,00 que diria que não há dinheiro.
  const mostraValor = valorDosNegocios || valorDosOrcamentos
  //: Enquanto o lote não chegou (ou falhou), as contagens são "—": zero diria que não há negócio.
  const negociosLidos = negocios.isSuccess

  if (grupo.isError) {
    const naoEncontrado = grupo.error instanceof ErroDaApi && grupo.error.status === 404
    return (
      <div className="grupo">
        <p className="grupo__erro" role="alert">
          {naoEncontrado ? GRUPO_NAO_ENCONTRADO : mensagemDe(grupo.error)}
        </p>
      </div>
    )
  }
  if (grupo.data === undefined) {
    return (
      <p className="grupo__ajuda" role="status">
        Carregando o grupo…
      </p>
    )
  }

  const nomeDaFilial = new Map(
    filiais.map((filial) => [filial.id, nomeExibidoDaEmpresa(filial)]),
  )
  const nomeDoResponsavel = (filial: FilialDoGrupo) =>
    filial.responsavel_id === null
      ? 'Sem responsável'
      : (responsaveis.data?.find((pessoa) => pessoa.id === filial.responsavel_id)?.nome ?? '—')

  const contagem = (quantidade: number) => (negociosLidos ? String(quantidade) : SEM_VALOR)
  const celulaDosOrcamentos = (quantidade: number) =>
    negociosLidos && orcamentosLidos ? String(quantidade) : SEM_VALOR
  const valorEmAndamento = (centavosDoValor: number) =>
    negociosLidos && valorDosNegocios ? emReais(centavosDoValor) : SEM_VALOR
  const valorDeOrcamentos = (centavosDoValor: number) =>
    negociosLidos && orcamentosLidos && valorDosOrcamentos ? emReais(centavosDoValor) : SEM_VALOR
  const limparAsRecusas = () => {
    for (const mutacao of [renomear, incluir, retirar, desfazer]) {
      mutacao.reset()
    }
    setErroDoNome(null)
  }

  const enviarONome = (evento: FormEvent) => {
    evento.preventDefault()
    if (tamanhoDoNome(nome) > TAMANHO_DO_NOME_DO_GRUPO) {
      setErroDoNome(NOME_LONGO_DEMAIS)
      return
    }
    limparAsRecusas()
    renomear.mutate(nome, {
      onSuccess: () => {
        setRenomeando(false)
      },
    })
  }

  const enviarAInclusao = (evento: FormEvent) => {
    evento.preventDefault()
    if (escolhida === null) return
    limparAsRecusas()
    incluir.mutate(escolhida.id, {
      onSuccess: () => {
        inclusao.limpar()
      },
    })
  }

  const tirar = (filial: FilialDoGrupo) => {
    //: Quem pede deixa de ler o Grupo se esta era a última que a tela mostra ou, fora da gestão, a
    //: única de que ele é Responsável (a leitura do FR-88).
    const gestao = sessao?.escopo_de_carteira === 'todas'
    const saindo =
      filiais.length === 1 ||
      (!gestao &&
        !filiais.some(
          (outra) => outra.id !== filial.id && outra.responsavel_id === sessao?.usuario_id,
        ))
    limparAsRecusas()
    retirar.mutate({ empresaId: filial.id, saindo }, {
      onSuccess: () => {
        if (saindo) {
          void navegar({ to: '/empresas/$empresaId', params: { empresaId: filial.id } })
        }
      },
    })
  }

  const desfazerOGrupo = () => {
    const destino = filiais[0]?.id
    limparAsRecusas()
    desfazer.mutate(undefined, {
      onSuccess: () => {
        if (destino === undefined) {
          void navegar({ to: '/empresas' })
        } else {
          void navegar({ to: '/empresas/$empresaId', params: { empresaId: destino } })
        }
      },
    })
  }

  const erroDasEscritas = [renomear, incluir, retirar, desfazer].find((m) => m.isError)?.error

  return (
    <div className="grupo">
      <section className="grupo__cartao" aria-labelledby="grupo-filiais-titulo">
        <div className="grupo__cabecalho">
          <h2 className="grupo__titulo" id="grupo-filiais-titulo">
            Filiais
          </h2>
          {podeGerir ? (
            <span className="grupo__formulario">
              <button
                type="button"
                className="grupo__acao"
                onClick={() => {
                  setNome(grupo.data.nome)
                  setRenomeando(true)
                }}
              >
                Renomear
              </button>
              <button
                type="button"
                className="grupo__acao"
                data-perigo="sim"
                onClick={() => {
                  setConfirmando(true)
                }}
              >
                Desfazer grupo
              </button>
            </span>
          ) : null}
        </div>
        <div className="grupo__corpo">
          {renomeando ? (
            <form className="grupo__formulario" onSubmit={enviarONome}>
              {/* 13.15: o `*` fora do `<label>`, como na montagem (`GrupoDaEmpresa`). */}
              <div className="grupo__campo">
                <LinhaDoRotulo obrigatorio>
                  <label htmlFor="grupo-nome">Nome do grupo</label>
                </LinhaDoRotulo>
                <input
                  id="grupo-nome"
                  value={nome}
                  aria-required
                  onChange={(evento) => {
                    setNome(evento.target.value)
                  }}
                />
              </div>
              <button
                type="submit"
                className="grupo__botao"
                disabled={nome.trim() === '' || renomear.isPending}
              >
                Salvar
              </button>
              <button
                type="button"
                className="grupo__acao"
                onClick={() => {
                  setRenomeando(false)
                  limparAsRecusas()
                }}
              >
                Cancelar
              </button>
            </form>
          ) : null}
          {confirmando ? (
            <div className="grupo__formulario" role="group" aria-label="Desfazer o grupo">
              <p className="grupo__ajuda">{CONFIRMACAO_DO_DESFAZER}</p>
              <button
                type="button"
                className="grupo__botao"
                disabled={desfazer.isPending}
                onClick={desfazerOGrupo}
              >
                Confirmar
              </button>
              <button
                type="button"
                className="grupo__acao"
                onClick={() => {
                  setConfirmando(false)
                }}
              >
                Cancelar
              </button>
            </div>
          ) : null}
          {erroDoNome !== null || erroDasEscritas !== undefined ? (
            <p className="grupo__erro" role="alert">
              {erroDoNome ?? mensagemDe(erroDasEscritas)}
            </p>
          ) : null}
          {veEspelho && falhasDosOrcamentos > 0 ? (
            <p className="grupo__erro" role="alert">
              {`Não foi possível ler os orçamentos de ${String(falhasDosOrcamentos)} ${
                falhasDosOrcamentos === 1 ? 'negócio' : 'negócios'
              }.`}
            </p>
          ) : null}
          {negocios.isError ? (
            <p className="grupo__erro" role="alert">
              {mensagemDe(negocios.error)}
            </p>
          ) : null}
          <div className="grupo__rolagem">
            <table className="grupo__tabela">
              <thead>
                <tr>
                  <th scope="col">Filial</th>
                  <th scope="col">CNPJ / CPF</th>
                  <th scope="col">Responsável</th>
                  <th scope="col" data-numero="sim">
                    Em andamento
                  </th>
                  <th scope="col" data-numero="sim">
                    Ganhos
                  </th>
                  <th scope="col" data-numero="sim">
                    Perdidos
                  </th>
                  <th scope="col" data-numero="sim">
                    Pedidos
                  </th>
                  <th scope="col" data-numero="sim">
                    Orçamentos
                  </th>
                  {mostraValor ? (
                    <>
                      <th scope="col" data-numero="sim">
                        Valor em andamento
                      </th>
                      <th scope="col" data-numero="sim">
                        Valor ganho
                      </th>
                      <th scope="col" data-numero="sim">
                        Valor dos orçamentos
                      </th>
                    </>
                  ) : null}
                  {podeEditarFilial ? (
                    <th scope="col" aria-label="Ações" />
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {filiais.map((filial) => {
                  const soma = porFilial.get(filial.id) ?? SOMA_VAZIA
                  const documento = filial.documento ?? null
                  return (
                    <tr key={filial.id}>
                      <td>
                        <Link to="/empresas/$empresaId" params={{ empresaId: filial.id }}>
                          {nomeDaFilial.get(filial.id)}
                        </Link>
                      </td>
                      <td>{documento === null ? SEM_VALOR : documentoNaSugestao(documento)}</td>
                      <td>{nomeDoResponsavel(filial)}</td>
                      <td data-numero="sim">{contagem(soma.andamento)}</td>
                      <td data-numero="sim">{contagem(soma.ganhos)}</td>
                      <td data-numero="sim">{contagem(soma.perdidos)}</td>
                      <td data-numero="sim">{contagem(soma.pedidos)}</td>
                      <td data-numero="sim">{celulaDosOrcamentos(soma.orcamentos)}</td>
                      {mostraValor ? (
                        <>
                          <td data-numero="sim">{valorEmAndamento(soma.valorEmAndamento)}</td>
                          <td data-numero="sim">{valorEmAndamento(soma.valorGanho)}</td>
                          <td data-numero="sim">{valorDeOrcamentos(soma.valorDosOrcamentos)}</td>
                        </>
                      ) : null}
                      {podeEditarFilial ? (
                        <td>
                          <button
                            type="button"
                            className="grupo__acao"
                            disabled={retirar.isPending}
                            aria-label={`Retirar ${nomeDaFilial.get(filial.id) ?? ''} do grupo`}
                            onClick={() => {
                              tirar(filial)
                            }}
                          >
                            Retirar
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  )
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={3}>Total</td>
                  <td data-numero="sim">{contagem(total.andamento)}</td>
                  <td data-numero="sim">{contagem(total.ganhos)}</td>
                  <td data-numero="sim">{contagem(total.perdidos)}</td>
                  <td data-numero="sim">{contagem(total.pedidos)}</td>
                  <td data-numero="sim">{celulaDosOrcamentos(total.orcamentos)}</td>
                  {mostraValor ? (
                    <>
                      <td data-numero="sim">{valorEmAndamento(total.valorEmAndamento)}</td>
                      <td data-numero="sim">{valorEmAndamento(total.valorGanho)}</td>
                      <td data-numero="sim">{valorDeOrcamentos(total.valorDosOrcamentos)}</td>
                    </>
                  ) : null}
                  {podeEditarFilial ? <td /> : null}
                </tr>
              </tfoot>
            </table>
          </div>
          {podeEditarFilial ? (
            <form className="grupo__formulario" onSubmit={enviarAInclusao}>
              <div className="grupo__campo">
                <label htmlFor={ID_DO_INCLUIR}>Incluir filial</label>
                <CampoDeEmpresa
                  key={inclusao.chaveDoCampo}
                  id={ID_DO_INCLUIR}
                  escolhida={inclusao.noCampo}
                  aoEscolher={inclusao.escolher}
                  placeholder={inclusao.lendo ? LENDO_A_EMPRESA : undefined}
                  erro={inclusao.erroDaLeitura}
                  {...(podeCriarEmpresa ? { aoCriarNova: inclusao.abrirAFicha } : {})}
                />
              </div>
              <button
                type="submit"
                className="grupo__botao"
                disabled={escolhida === null || inclusao.lendo || incluir.isPending}
              >
                Incluir
              </button>
              <p className="grupo__ajuda">{NOTA_DA_INCLUSAO}</p>
            </form>
          ) : null}
          {/* Irmão do `<form>`, e não filho: o `submit` da ficha borbulha pela árvore do React e
              chegaria ao "Incluir" (13.15). A Empresa gravada volta escolhida; "Incluir" grava.
              Sem o cartão de Pessoas. */}
          {inclusao.fichaAberta === null ? null : (
            <ModalDaNovaEmpresa
              textoInicial={inclusao.fichaAberta}
              // As Pessoas da filial entram pela ficha dela: uma linha de Pessoa aqui poderia
              // adotar um contato do WhatsApp sem que a tela do Grupo avisasse (13.15).
              semCartaoDePessoas
              aoCancelar={inclusao.fecharAFicha}
              aoEscolher={(empresa) => {
                limparAsRecusas()
                inclusao.aoCriar(empresa)
              }}
              aoEncontrar={(empresaId) => {
                limparAsRecusas()
                inclusao.aoEncontrar(empresaId)
              }}
            />
          )}
        </div>
      </section>

      <section className="grupo__cartao" aria-labelledby="grupo-negocios-titulo">
        <div className="grupo__cabecalho">
          <h2 className="grupo__titulo" id="grupo-negocios-titulo">
            Negócios do grupo
          </h2>
        </div>
        <div className="grupo__corpo">
          {(negocios.data ?? []).length === 0 ? (
            <p className="grupo__ajuda">
              {negocios.isLoading
                ? 'Carregando os negócios…'
                : negocios.isError
                  ? 'Não foi possível ler os negócios.'
                  : 'Nenhum negócio que você veja.'}
            </p>
          ) : (
            <div className="grupo__rolagem">
              <table className="grupo__tabela">
                <thead>
                  <tr>
                    <th scope="col">Negócio</th>
                    <th scope="col">Filial</th>
                    <th scope="col">Status</th>
                    {mostraValor ? (
                      <th scope="col" data-numero="sim">
                        Valor
                      </th>
                    ) : null}
                    <th scope="col">Pedido</th>
                  </tr>
                </thead>
                <tbody>
                  {(negocios.data ?? []).map((negocio) => (
                    <tr key={negocio.id}>
                      <td>
                        {/* 13.14b (DW-435): o modal sobre esta ficha, e não sobre Hoje. */}
                        <LinkDoNegocio negocioId={negocio.id}>
                          {`#${String(negocio.codigo)}`}
                        </LinkDoNegocio>
                      </td>
                      <td>{nomeDaFilial.get(negocio.empresa_id) ?? SEM_VALOR}</td>
                      <td>{ROTULO_DO_STATUS[negocio.status]}</td>
                      {mostraValor ? (
                        <td data-numero="sim">
                          {'valor' in negocio && typeof negocio.valor === 'string'
                            ? emReais(centavos(negocio.valor))
                            : SEM_VALOR}
                        </td>
                      ) : null}
                      <td>{negocio.numero_do_pedido ?? SEM_VALOR}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}
